import { observer } from 'mobx-react';

import styles from './CompositePriceEditor.module.scss';

import { Nullable } from 'chaika-common-types';

import { Money } from '@store/models/Money';

import { useEditor } from './hooks/useEditor';
import { Button } from '@components/Button';
import { OptionType } from '@components/Select';
import { CompositePrice } from './CompositePrice';
import { translate } from '@chaika/i18n/helper';

interface Props {
    disabled?: boolean;
    title?: string;
    dataTest: string;
    includedServices: string[];
    legalEntityIds?: string[];
    onChange(includedServices: string[]): void;
}

export const CompositePriceEditor = observer(
    ({ includedServices = [], dataTest, onChange, disabled, title, legalEntityIds }: Props) => {
        const { rows, total, currency, onAddClick, onItemChange, disabledButton, onDelete } = useEditor({
            includedServices,
            onChange,
            legalEntityIds,
        });

        return (
            <div className={styles.root}>
                <div className={styles.title}>{title}</div>
                {rows.map(({ serviceId, price }, index) => (
                    <CompositePrice
                        key={index}
                        disabled={disabled}
                        onDelete={() => onDelete(index)}
                        serviceId={serviceId}
                        currency={currency}
                        price={price}
                        onItemChange={(params: Nullable<OptionType>) => onItemChange(params, index)}
                        dataTest={`${dataTest}_serviceSelect_${index}`}
                        isClearable={rows.length === index + 1}
                    />
                ))}
                <div className={styles.total}>
                    {translate('totalSecond')}: {new Money(total, currency).toString({ withoutKopecks: true })}
                </div>
                <div className={styles.button}>
                    <Button
                        disabled={disabledButton || disabled}
                        label={translate('addService')}
                        dataTest={`${dataTest}_addButton`}
                        onClick={onAddClick}
                    />
                </div>
            </div>
        );
    },
);
